import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SortableList } from "@/components/SortableList";
import { GlyphCanvas, GlyphView, EMPTY_GLYPH } from "@/components/language/GlyphCanvas";
import { api, errMsg } from "@/lib/api";
import { useInvalidate } from "@/lib/queries";

export const TYPES = ["letter", "syllable", "word", "other"];
export const useGlyphs = (lid) => useQuery({ queryKey: ["glyphs", lid], queryFn: () => api.get(`/languages/${lid}/glyphs`).then((r) => r.data), enabled: !!lid });

function GlyphDialog({ lang, initial, onOpenChange }) {
  const invalidate = useInvalidate();
  const [form, setForm] = useState({ pixels: EMPTY_GLYPH, name: "", sound: "", meaning: "", notes: "", type: "letter", ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    setBusy(true);
    const body = { pixels: form.pixels, name: form.name, sound: form.sound, meaning: form.meaning, notes: form.notes, type: form.type };
    try {
      if (initial?.id) await api.patch(`/glyphs/${initial.id}`, body);
      else await api.post(`/languages/${lang.id}/glyphs`, body);
      invalidate(["glyphs", lang.id]);
      toast.success("Symbol saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  const remove = async () => {
    await api.delete(`/glyphs/${initial.id}`);
    invalidate(["glyphs", lang.id], ["words", lang.id]);
    onOpenChange(false);
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto lf-scroll" data-testid="glyph-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">{initial?.id ? "Edit symbol" : `New ${lang.name} symbol`}</DialogTitle>
          <DialogDescription>Draw on the 60×60 grid in black on white.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[360px_1fr]">
          <GlyphCanvas pixels={form.pixels} onChange={set("pixels")} />
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={set("name")} placeholder="kesh-rune" data-testid="glyph-name" /></div>
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={form.type} onValueChange={set("type")}><SelectTrigger data-testid="glyph-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Sound it represents</Label><Input value={form.sound} onChange={set("sound")} placeholder="/k/, 'kesh'" data-testid="glyph-sound" /></div>
            <div className="space-y-1.5"><Label>Meaning</Label><Input value={form.meaning} onChange={set("meaning")} placeholder="fire (for word symbols)" data-testid="glyph-meaning" /></div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={4} value={form.notes} onChange={set("notes")} data-testid="glyph-notes" /></div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          {initial?.id && <Button variant="ghost" className="mr-auto text-red-400" onClick={remove} data-testid="glyph-delete-button">Delete</Button>}
          <Button onClick={save} disabled={busy || !form.pixels.includes("1")} data-testid="glyph-save-button">{busy ? "Saving…" : "Save symbol"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ScriptTab({ lang, canEdit }) {
  const { data: glyphs = [] } = useGlyphs(lang.id);
  const invalidate = useInvalidate();
  const [editing, setEditing] = useState(null);
  const reorder = async (next) => {
    await api.put(`/languages/${lang.id}/glyphs/order`, { ids: next.map((g) => g.id) });
    invalidate(["glyphs", lang.id]);
  };
  const tile = (g) => (
    <button onClick={() => canEdit && setEditing(g)} className="w-full rounded-md border border-[#2D3442] bg-[#13171F] p-3 text-left transition-colors hover:border-[#14B8A6]/60" data-testid={`glyph-tile-${g.id}`}>
      <GlyphView pixels={g.pixels} size={72} />
      <div className="mt-2 truncate text-sm text-[#F3EBDD]">{g.name || g.sound || "Untitled"}</div>
      <div className="truncate text-xs text-[#A8A090]">{g.type}{g.sound && ` · ${g.sound}`}{g.meaning && ` · ${g.meaning}`}</div>
    </button>
  );
  return (
    <div data-testid="script-tab">
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-[#A8A090]">The symbols of {lang.name}'s script. {canEdit && "Drag to set alphabet order."}</p>
        {canEdit && <Button onClick={() => setEditing({})} data-testid="new-glyph-button"><Plus className="h-4 w-4 mr-1" />New symbol</Button>}
      </div>
      {glyphs.length === 0 ? (
        <div className="rounded-md border border-dashed border-[#2D3442] p-12 text-center" data-testid="script-empty"><p className="font-display text-3xl">No symbols drawn yet.</p></div>
      ) : canEdit ? (
        <SortableList scope="glyphs" items={glyphs} getKey={(g) => g.id} onReorder={reorder} render={tile} className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6" />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">{glyphs.map((g) => <div key={g.id}>{tile(g)}</div>)}</div>
      )}
      {editing && <GlyphDialog lang={lang} initial={editing.id ? editing : null} onOpenChange={() => setEditing(null)} />}
    </div>
  );
}

export function GlyphSpelling({ ids = [], glyphs, size = 22 }) {
  const map = Object.fromEntries(glyphs.map((g) => [g.id, g]));
  const shown = ids.map((i) => map[i]).filter(Boolean);
  if (!shown.length) return null;
  return <div className="flex flex-wrap gap-0.5" data-testid="glyph-spelling">{shown.map((g, i) => <GlyphView key={i} pixels={g.pixels} size={size} />)}</div>;
}

export function GlyphComposer({ value, onChange, glyphs }) {
  if (!glyphs.length) return <p className="text-xs text-[#A8A090]">Draw symbols in the Script tab to write words with them.</p>;
  return (
    <div className="space-y-2" data-testid="glyph-composer">
      <div className="flex min-h-[40px] flex-wrap items-center gap-1 rounded-md border border-[#2D3442] bg-[#0F131A] p-1.5">
        {value.length === 0 && <span className="px-1 text-xs text-[#A8A090]">Click symbols below to spell the word</span>}
        {value.map((id, i) => {
          const g = glyphs.find((x) => x.id === id);
          return g && <button type="button" key={i} title="Remove" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`composer-slot-${i}`}><GlyphView pixels={g.pixels} size={28} /></button>;
        })}
      </div>
      <div className="flex flex-wrap gap-1">
        {glyphs.map((g) => <button type="button" key={g.id} title={g.sound || g.name} onClick={() => onChange([...value, g.id])} className="rounded border border-[#2D3442] p-0.5 hover:border-[#14B8A6]" data-testid={`composer-pick-${g.id}`}><GlyphView pixels={g.pixels} size={26} /></button>)}
      </div>
    </div>
  );
}
